import { Formik, useFormikContext } from "formik";
import { ChevronDown, Plus, Search } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { useIsChromeExtension } from "@src/features/chrome/providers/isChromeExtension";
import { usePatientFromIframe } from "@src/features/iframe";
import {
  PatientSearch,
  PatientSearchProps,
} from "@src/shared/components/data/PatientSearch";
import { FormikDateInput } from "@src/shared/components/forms/FormikDateInput";
import { FormikInput } from "@src/shared/components/forms/FormikInput";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";
import { NaiveDate } from "@src/shared/utils/types";

import { BulkDocumentUpload } from "./BulkDocumentUpload";
import {
  CreatePatientDialog,
  type CreatePatientSubmitValues,
} from "./CreatePatientDialog";

export interface PatientSearchFormValues {
  q?: string | null;
  date_of_birth?: NaiveDate | null;
  zip_code?: string | null;
}

const validationSchema = yup.object<PatientSearchFormValues>({
  q: yup.string().nullable(true),
  date_of_birth: (yup.mixed() as yup.Schema<NaiveDate | null>).nullable(true),
  zip_code: yup.string().nullable(true),
});

export interface PatientsPageProps extends PatientSearchProps {
  canCreatePatient?: boolean;
  isCreatingPatient?: boolean;
  createPatientError?: string | null;
  onSearch: (values: PatientSearchFormValues) => void;
  onCreatePatientDialogOpenChange?: (open: boolean) => void;
  onCreatePatient: (values: CreatePatientSubmitValues) => void | Promise<void>;
}

const PatientsPageForm = ({
  canCreatePatient = false,
  isCreatingPatient = false,
  createPatientError = null,
  onCreatePatientDialogOpenChange,
  onCreatePatient,
}: {
  canCreatePatient?: boolean;
  isCreatingPatient?: boolean;
  createPatientError?: string | null;
  onCreatePatientDialogOpenChange?: (open: boolean) => void;
  onCreatePatient: (values: CreatePatientSubmitValues) => void | Promise<void>;
}) => {
  const { values, handleSubmit } = useFormikContext<PatientSearchFormValues>();
  const [, setSearchParams] = useSearchParams();
  const isChromeExtension = useIsChromeExtension();
  const [createPatientDialogOpen, setCreatePatientDialogOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchFieldsId = useId();

  const handleCreatePatientDialogOpenChange = (open: boolean) => {
    setCreatePatientDialogOpen(open);
    onCreatePatientDialogOpenChange?.(open);
  };

  useEffect(() => {
    setSearchParams(
      {
        date_of_birth: values.date_of_birth
          ? NaiveDate.toString(values.date_of_birth)
          : "",
        q: values.q ?? "",
        zip_code: values.zip_code ?? "",
      },
      { replace: true },
    );
  }, [values, setSearchParams]);

  return (
    <VerticalFlexbox
      as="form"
      gap="md"
      onSubmit={handleSubmit}
      className="w-full rounded-xl border border-border bg-card p-4 shadow-soft sm-stitch:p-5 lg-stitch:p-6"
    >
      <VerticalFlexbox gap="xs">
        <div className="flex flex-col gap-4 lg-stitch:flex-row lg-stitch:items-start lg-stitch:justify-between">
          <VerticalFlexbox gap="xs" className="min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <HeaderText
                  tag="h1"
                  fontWeight={600}
                  className="text-2xl sm-stitch:text-3xl"
                >
                  Patients
                </HeaderText>
                <Text
                  sizing="sm"
                  appearance="subdued"
                  className="max-w-prose leading-6"
                >
                  Import a patient's gap list to build their visit agenda, or
                  open a record to get started.
                </Text>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 sm-stitch:hidden"
                aria-expanded={isSearchExpanded}
                aria-controls={searchFieldsId}
                onClick={() => setIsSearchExpanded((expanded) => !expanded)}
              >
                {isSearchExpanded ? "Hide" : "Filters"}
                <ChevronDown
                  className={cn(
                    "size-4 transition-transform",
                    isSearchExpanded && "rotate-180",
                  )}
                />
              </Button>
            </div>
          </VerticalFlexbox>
          <div className="grid w-full grid-cols-1 gap-2 sm-stitch:grid-cols-2 lg-stitch:w-auto lg-stitch:flex lg-stitch:items-center">
            {canCreatePatient ? (
              <Button
                type="button"
                size="sm"
                className="w-full lg-stitch:w-auto"
                onClick={() => handleCreatePatientDialogOpenChange(true)}
              >
                <Plus />
                Create Patient
              </Button>
            ) : null}
            <BulkDocumentUpload />
          </div>
        </div>
      </VerticalFlexbox>
      <div
        id={searchFieldsId}
        className={cn(
          "grid grid-cols-1 gap-3 sm-stitch:grid sm-stitch:grid-cols-2 lg-stitch:grid-cols-[minmax(14rem,2fr)_minmax(10rem,1fr)_minmax(8rem,1fr)]",
          !isSearchExpanded && "hidden",
        )}
      >
        <FormikInput
          name="q"
          label="Search"
          placeholder="Search by patient name"
        />
        <FormikDateInput name="date_of_birth" label="Date of birth" />
        <FormikInput name="zip_code" label="Zip code" />
      </div>
      <HorizontalFlexbox
        justifyContent="flex-end"
        className={cn("w-full sm-stitch:flex", !isSearchExpanded && "hidden")}
      >
        <Button
          type="submit"
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90 sm-stitch:w-auto"
        >
          <Search className="mr-1 size-4" />
          Search
        </Button>
      </HorizontalFlexbox>
      <CreatePatientDialog
        open={createPatientDialogOpen}
        isSubmitting={isCreatingPatient}
        error={createPatientError}
        showOverlay={!isChromeExtension}
        onOpenChange={handleCreatePatientDialogOpenChange}
        onSubmit={onCreatePatient}
      />
    </VerticalFlexbox>
  );
};

export const PatientsPage = ({
  patients,
  searchErrors,
  isLoading,
  isError,
  onSearch,
  sortBy,
  order,
  onSortChange,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
  canCreatePatient,
  isCreatingPatient,
  createPatientError,
  onCreatePatientDialogOpenChange,
  onCreatePatient,
}: PatientsPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientFromIframe = usePatientFromIframe();

  const dobFromSearchParams = searchParams.get("date_of_birth");

  const handleSearch = async (values: PatientSearchFormValues) => {
    onSearch(values);
    setSearchParams({
      date_of_birth: values.date_of_birth
        ? NaiveDate.toString(values.date_of_birth)
        : "",
      q: values.q ?? "",
      zip_code: values.zip_code ?? "",
    });
  };

  const iframeQ = [patientFromIframe?.first_name, patientFromIframe?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <VerticalFlexbox
      gap="md"
      className="w-full px-3 py-4 sm-stitch:px-4 md-stitch:px-8 md-stitch:py-8"
    >
      <Formik<PatientSearchFormValues>
        initialValues={{
          q: iframeQ || searchParams.get("q") || null,
          date_of_birth: patientFromIframe?.date_of_birth
            ? NaiveDate.fromString(patientFromIframe.date_of_birth)
            : dobFromSearchParams
              ? NaiveDate.fromString(dobFromSearchParams)
              : null,
          zip_code:
            patientFromIframe?.zip_code ?? searchParams.get("zip_code") ?? null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSearch}
      >
        <PatientsPageForm
          canCreatePatient={canCreatePatient}
          isCreatingPatient={isCreatingPatient}
          createPatientError={createPatientError}
          onCreatePatientDialogOpenChange={onCreatePatientDialogOpenChange}
          onCreatePatient={onCreatePatient}
        />
      </Formik>
      <PatientSearch
        patients={patients}
        searchErrors={searchErrors}
        isLoading={isLoading}
        isError={isError}
        sortBy={sortBy}
        order={order}
        onSortChange={onSortChange}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
      />
    </VerticalFlexbox>
  );
};
